import { useEffect, useRef, useState } from "react";
import LanguageSwitcher from "./components/LanguageSwitcher";
import UserAvatar from "./components/UserAvatar";
import CTAButton from "./components/CTAButton";
import MobileNav from "./components/MobileNav";
import { makeId } from "./lib/id";
import NavBar from "./components/NavBar";
import { dict, getInitialLang, type Lang } from "./lib/i18n";
import {
  initTG,
  getTG,
  getAvatarUrl,
  getUserName,
  vibrate,
  vibrateSuccess,
  vibrateTick,
} from "./lib/telegram";
import { openWS, fetchHistory } from "./lib/api";

// Тип одного сообщения
type Msg = { role: "user" | "assistant"; content: string };

export default function App() {
  // --- базовые состояния
  const [session] = useState(() => makeId());
  const [tgId, setTgId] = useState<number | null>(null);

  const [lang, setLang] = useState<Lang>(() =>
    getInitialLang(getTG()?.initDataUnsafe?.user?.language_code ?? null)
  );
  const t = (k: keyof typeof dict["ru"]) => dict[lang][k];

  const [msgs, setMsgs] = useState<Msg[]>([
    { role: "assistant", content: dict[lang].hello },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // --- init Telegram + определяем tgId + подгружаем историю
  useEffect(() => {
    const tg = initTG();
    const id = tg?.initDataUnsafe?.user?.id ?? null;
    setTgId(id);

    if (!id) return; // вне Telegram — историю не грузим
    fetchHistory(id, 10)
      .then((res) => {
        if (res.items?.length) {
          const history: Msg[] = res.items.map((it) => ({
            role: (it.role === "assistant" ? "assistant" : "user") as Msg["role"],
            content: it.content,
          }));
          setMsgs(history);
        }
      })
      .catch(() => {});
  }, []);

  // авто-скролл вниз
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [msgs.length]);

  // смена языка — обновляем приветствие, если оно единственное
  useEffect(() => {
    setMsgs((m) =>
      m.length === 1 && m[0].role === "assistant"
        ? [{ role: "assistant", content: dict[lang].hello }]
        : m
    );
  }, [lang]);

  // имя/аватар из Telegram
  const name = getUserName();
  const avatar = getAvatarUrl();

  // переключатель языка
  const toggleLang = () => {
    setLang((l) => {
      const next: Lang = l === "ru" ? "uz" : "ru";
      localStorage.setItem("lang", next);
      return next;
    });
  };

  // --- отправка через WebSocket (передаём user_id)
  async function sendWS() {
    if (!tgId) return; // вне Telegram — чата нет
    const text = input.trim();
    if (!text) return;

    vibrate(); // клик

    const user: Msg = { role: "user", content: text };
    setMsgs((m) => [...m, user, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    const ws = openWS();
    let idx = -1;
    let firstChunk = true;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          session_id: session,
          user_id: tgId, // <-- важное поле
          messages: [...msgs, user],
        })
      );
    };

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "chunk") {
        if (firstChunk) {
          vibrateTick(); // первый токен
          firstChunk = false;
        }
        setMsgs((m) => {
          if (idx < 0) idx = m.length - 1;
          const copy = m.slice();
          copy[idx] = {
            role: "assistant",
            content: (copy[idx].content || "") + data.delta,
          };
          return copy;
        });
      } else if (data.type === "done") {
        setStreaming(false);
        vibrateSuccess(); // завершение
        ws.close();
      }
    };

    ws.onerror = () => {
      setStreaming(false);
      ws.close();
    };
  }

  // ---- UI
  return (
      <div className="min-h-screen-svh bg-grid relative text-white overscroll-contain">

        <div className="absolute inset-0 grid-lines pointer-events-none"></div>
        <div
            className="
      absolute left-1/2 -translate-x-1/2
      z-20
      top-[calc(env(safe-area-inset-top))]
    "
        >
          <div
              className="
          inline-flex items-center gap-2 text-[10px] md:text-xs
          font-semibold px-3 py-1 rounded-full glass
        "
          >
            <span className="size-2 rounded-full bg-emerald-400"></span>
            {t("badge")}
          </div>
        </div>

        {/* HEADER */}
        <header
            className="
        tg-safe-header
        relative z-10 max-w-6xl mx-auto
        h-12 md:h-16 flex items-center justify-between
        px-3 md:px-4
      "
        >
          <div className="flex items-center gap-2">
            <UserAvatar name={name} src={avatar ?? undefined}/>
            <span className="text-xs md:text-sm text-white/70">
      {dict[lang].welcome} {name}
    </span>
          </div>

          <div className="hidden md:flex">
            <NavBar/>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher value={lang} onToggle={toggleLang}/>
            {/*<a className="text-xs md:text-sm text-white/70 hover:text-white transition"*/}
            {/*   href="https://github.com/lexaowner" target="_blank" rel="noreferrer">GitHub</a>*/}
            {/*<a className="text-xs md:text-sm text-white/70 hover:text-white transition" href="https://t.me/EMWCL"*/}
            {/*   target="_blank" rel="noreferrer">TG</a>*/}
          </div>
        </header>
        <MobileNav>
          <NavBar compact/>
        </MobileNav>


        {/* MAIN */}
        <main className="relative z-10 max-w-6xl mx-auto px-3 md:px-4 pt-1 md:pt-4">


          {/* Banner */}
          <div className="mt-4 mb-6">
            <a
                href="/#/generate"
                target="_blank"
                rel="noreferrer"
                className="
                  block w-full rounded-2xl glass p-4 md:p-5
                  bg-gradient-to-r from-indigo-500/20 via-purple-500/10 to-fuchsia-500/20
                  border border-white/10
                  transition hover:bg-white/10 hover:border-white/20
                  cursor-pointer
                "
            >
              <div className="text-center md:text-left">
                <h3 className="text-lg md:text-xl font-semibold">
                  ✨ Генерация изображений здесь
                </h3>
                <p className="text-white/60 text-sm mt-1">
                  В мини-приложении, пробуй сейчас!
                </p>
              </div>
            </a>
          </div>


          {/* CONTENT */}
          <section className="mt-6 md:mt-8">
            <div className="glass rounded-2xl md:rounded-3xl p-3 md:p-6
                              h-[60svh] md:h-[calc(90vh-230px)]
                              flex flex-col">
              {tgId ? (
                  <>
                    <div className="text-sm text-white/70 mb-2">{t("live_chat")}</div>
                    <div ref={listRef} className="flex-1 overflow-y-auto pr-1 space-y-3">
                      {msgs.map((m, i) => (
                          <div
                              key={i}
                              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div
                                className={`max-w-[85%] md:max-w-[80%] rounded-2xl px-3 md:px-4 py-2 shadow ${
                                    m.role === "user"
                                        ? "bg-indigo-500/80"
                                        : "bg-white/5 border border-white/10"
                                }`}
                            >
                              <div className="whitespace-pre-wrap leading-relaxed text-[15px] md:text-base">
                                {m.content}
                              </div>
                            </div>
                          </div>
                      ))}
                      {streaming && (
                          <div className="text-xs text-white/50 animate-pulse">
                            model is typing…
                          </div>
                      )}
                    </div>

                    {/* INPUT + SEND */}
                    <div className="mt-2 md:mt-3 sticky bottom-0 safe-bottom bg-transparent">
                      <div className="flex gap-2">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) sendWS();
                            }}
                            placeholder={t("placeholder")}
                            className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 md:px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={sendWS}
                            disabled={streaming}
                            className="rounded-xl px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60"
                        >
                          {t("btn_send")}
                        </button>
                      </div>
                    </div>
                  </>
              ) : (
                  <div className="flex flex-col items-center justify-center text-center gap-4 flex-1">
                    <p className="text-white/70 max-w-md">
                      Чтобы пользоваться чатом, откройте мини-приложение в Telegram.
                    </p>
                    <CTAButton as="a" href="https://t.me/gpt_5UzBot?startapp=1">
                      Попробовать в Telegram
                    </CTAButton>
                  </div>
              )}
            </div>
            {/* RIGHT: Возможности GPT-5 */}
            {/*<div className="glass rounded-2xl md:rounded-3xl p-4 md:p-6 flex flex-col justify-between">*/}
            {/*  <div>*/}
            {/*    <h3 className="text-xl md:text-2xl font-semibold">{t("right_title")}</h3>*/}
            {/*    <p className="text-white/70 mt-2 text-sm md:text-base">{t("right_p")}</p>*/}
            {/*    <ul className="text-xs md:text-sm text-white/70 mt-3 space-y-2 list-disc pl-5">*/}
            {/*      <li>{t("li1")}</li>*/}
            {/*      <li>{t("li2")}</li>*/}
            {/*      <li>{t("li3")}</li>*/}
            {/*      <li>{t("li4")}</li>*/}
            {/*      <li>{t("li5")}</li>*/}
            {/*      <li>{t("subtitle")}</li>*/}
            {/*    </ul>*/}
            {/*  </div>*/}
            {/*  <div className="flex gap-2 md:gap-3 mt-4">*/}
            {/*    <CTAButton onClick={() => getTG()?.HapticFeedback?.impactOccurred("medium")}>*/}
            {/*      Пробуй сейчас*/}
            {/*    </CTAButton>*/}
            {/*    <CTAButton as="a" href="https://t.me/EMWCL">*/}
            {/*      Сотрудничество*/}
            {/*    </CTAButton>*/}
            {/*  </div>*/}
            {/*</div>*/}
          </section>

        </main>
      </div>
  );
}
